import BookCover from "@/components/BookCover";
import EmailCapture from "@/components/EmailCapture";
import { HeroLine, Reveal } from "@/components/Motion";
import { AMAZON_URL, KINDLE_URL } from "@/lib/site";

const faqs = [
  {
    q: "Is this a religious book?",
    a: "No. The book engages with several religious traditions over the course of the author's life, but it is a memoir, not a work of theology or apologetics. It does not ask the reader to believe anything.",
  },
  {
    q: "Do I need any background to read it?",
    a: "None. The book explains every tradition and term it touches as it arrives at them, in the order the author encountered them.",
  },
  {
    q: "Is the book based on Hindu philosophy?",
    a: "The final third of the book engages with Advaita Vedanta — the non-dual tradition associated with Adi Shankara — because that is what the author eventually encountered. The first two-thirds are about Christianity, Buddhism, and the search itself. Readers from any background, or none, will find it accessible.",
  },
];

const sample = `The first time I heard the word "seeking," I was twenty-four years old, sitting in the back of a small church in Pasadena that had once been a movie theatre. The pastor used the word the way other people might use the word "groceries" — casually, as if it described a thing one ordinarily did. I had not, until that moment, considered that what I had been doing for some years already had a name.

I had been raised in a particular kind of careful Indian household where God was acknowledged but not pursued. We attended temple on the appropriate days, recited what we had been given to recite, and then went home to a life that ran on more legible currencies. My father worked. My mother worked. My sister and I were sent to schools that would deliver us to careers that would, in their turn, deliver us to lives that resembled, with small adjustments, the lives of our parents.

The arrangement was not unkind. It was simply complete. There was no room in it for a question that did not already have an answer somewhere in the family's accumulated knowledge. And so when the question came — and it did come, sometime in my last year of college, with no warning and no obvious cause — I had nowhere to put it. I went looking for a place to put it. The looking is what this book is about.

What I will say at the outset is this: the looking lasted almost twenty years. It cost more than I expected. It produced certificates and practices and several thousand pages of notes that I will, in time, throw away. It did not produce, until very near the end, the thing I was looking for. And when the thing I was looking for finally arrived, it did not arrive as the result of anything I had done. It arrived as the recognition of something that had been present the whole time, on the other side of every door I had been knocking on.

I want to be careful here, because this is the kind of sentence that, written by someone else, would have made me close the book. I am not saying I had a sudden insight and now I am wise. I am saying that, after a great deal of effort that turned out to be largely beside the point, I noticed something I had been failing to notice. The noticing was not dramatic. It was, if anything, embarrassingly ordinary…`;

export default function BookPage() {
  return (
    <>
      <section data-testid="book-hero-section" className="min-h-[calc(92svh-6rem)] flex items-center py-12 md:py-20" style={{ backgroundColor: "var(--paper)" }}>
        <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
          <div className="lg:col-span-7 order-1">
            <p className="overline mb-6 animate-overline">The Book</p>
            <h1 data-testid="book-hero-title" className="font-serif-lit text-5xl sm:text-6xl leading-[1]" style={{ fontWeight: 500 }}>
              <HeroLine delay={0.1}>Nothing in</HeroLine>
              <HeroLine delay={0.22} className="italic">the Way</HeroLine>
            </h1>
            <p className="font-serif-lit italic text-2xl md:text-3xl mt-6 hero-copy-reveal" style={{ color: "var(--ink-soft)" }}>A Memoir</p>
            <div className="mt-10 flex flex-wrap gap-4 hero-copy-reveal hero-copy-delay">
              <a href={AMAZON_URL} target="_blank" rel="noreferrer" data-testid="book-buy-amazon" className="btn-ink">Buy on Amazon</a>
              <a href={KINDLE_URL} target="_blank" rel="noreferrer" data-testid="book-buy-kindle" className="btn-ghost">Buy on Kindle</a>
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center lg:justify-end order-2 hero-cover-reveal">
            <BookCover size="lg" testId="book-page-cover" interactive />
          </div>
        </div>
      </section>

      <section data-testid="book-about-section" className="py-24 md:py-32" style={{ backgroundColor: "var(--paper-deep)" }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal><p className="overline mb-8">What the book is about</p></Reveal>
          <Reveal className="font-serif-lit text-xl md:text-2xl leading-[1.7] space-y-7" delay={0.08}>
            <p>Most spiritual memoirs are stories of arrival. <em className="italic">Nothing in the Way</em> is the story of the search itself — the years of moving between traditions, the certificates collected, the practices abandoned, the slow accumulation of evidence that something was still missing.</p>
            <p>It is the story of a man who did everything seekers are supposed to do, and who eventually had to admit, against his own preference, that the seeking was the obstacle.</p>
            <p>The book follows two decades of one life: California in the early years, the encounter with Christianity, the long immersion in Vipassana and Buddhist meditation, the smaller traditions that came and went, the marriage that strained and held, the daughter who watched her father search, and finally — quietly, almost by accident — the encounter with a teaching from his own civilisation that had been there the whole time.</p>
            <p>It is not a book about Indian philosophy. It is a book about a person, who arrived at Indian philosophy after every other door had closed.</p>
          </Reveal>
        </div>
      </section>

      <section id="sample" data-testid="book-sample-section" className="py-24 md:py-32" style={{ backgroundColor: "var(--paper)" }}>
        <div className="max-w-[820px] mx-auto px-6 md:px-12">
          <Reveal><p className="overline mb-6">Read a sample</p></Reveal>
          <Reveal delay={0.06}>
            <h2 data-testid="book-sample-title" className="font-serif-lit text-3xl md:text-4xl mb-10" style={{ fontWeight: 500 }}>
              Chapter 1 — Standing Under the Wrong Sky
            </h2>
          </Reveal>
          <Reveal
            testId="book-sample-text"
            className="sample-copy font-serif-lit text-lg md:text-xl leading-[1.85] whitespace-pre-line"
            delay={0.1}
          >
            {sample}
          </Reveal>
        </div>
      </section>

      <section data-testid="faq-section" className="py-24 md:py-32 border-y" style={{ backgroundColor: "var(--paper-deep)", borderColor: "var(--rule)" }}>
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal><p className="overline mb-10">Frequently asked questions</p></Reveal>
          <ul className="flex flex-col">
            {faqs.map((faq, index) => (
              <Reveal as="li" key={faq.q} testId={`faq-item-${index}`} className="py-9 border-t first:border-t-0" delay={index * 0.06}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-10">
                  <span className="manifesto-number md:col-span-1">0{index + 1}</span>
                  <div className="md:col-span-11">
                    <h2 className="font-serif-lit text-2xl md:text-3xl mb-4" style={{ fontWeight: 500 }}>{faq.q}</h2>
                    <p className="font-serif-lit text-lg leading-relaxed" style={{ color: "var(--ink-soft)" }}>{faq.a}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section data-testid="book-buy-section" className="py-24 md:py-32" style={{ backgroundColor: "var(--ink)", color: "var(--paper)" }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal><p className="overline mb-12" style={{ color: "var(--warm)" }}>Buy</p></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px" style={{ backgroundColor: "rgba(249,246,240,0.2)" }}>
            {[
              { label: "Amazon India", href: AMAZON_URL, id: "buy-card-amazon-in" },
              { label: "Kindle", href: KINDLE_URL, id: "buy-card-kindle" },
            ].map((store) => (
              <a key={store.id} href={store.href} target="_blank" rel="noreferrer" data-testid={store.id} className="store-link group">
                <span className="font-serif-lit text-3xl md:text-4xl">{store.label}</span>
                <span aria-hidden="true" className="store-arrow">↗</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <EmailCapture testIdPrefix="book-newsletter" />
    </>
  );
}